import { type FC, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { observer } from 'mobx-react-lite'
import { IS_APP_LIGHT } from 'shared/config'
import {
  Button,
  ContainerHeader,
  EnumIconButtonShape,
  Icon,
  IconButton,
  layoutStore,
  Tooltip,
  Typography,
} from 'shared/ui'
import { type PageLayoutStore } from 'shared/layout'
import { uiStore } from 'shared/store/uiStore'
import { FeatureRestrictedModalStore } from 'entities/UsersPermissions'
import { type Phone } from 'entities/Phone/model/Phone'
import { PhoneRenderContent } from 'entities/Phone'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { Inbox } from 'entities/Inbox/model/Inbox'
import type { CallModalStore } from 'widgets/CallModal'
import type { ContactCreateModalStore } from 'widgets/ContactCreateModal'
import { ConversationHeaderListDropdown } from 'pages/conversations/ui/ConversationHeaderListDropdown'
import type { ConversationSearchStore } from 'pages/conversations/ui/ConversationHeaderSearch'
import { ConversationRoutes } from 'pages/conversations/route/type'
import { type ConversationMenuStore } from 'pages/conversations/ui/ConversationMenu'
import { useConversationsPageLayoutContext } from 'pages/conversations/context/conversationsPageLayoutContext'
import styles from './styles.module.scss'

type IConversationHeaderProps = {
  pageLayoutStore: PageLayoutStore
  conversationSearchStore: ConversationSearchStore
  callModalStore: CallModalStore
  contactCreateModalStore: ContactCreateModalStore
  conversationMenuStore: ConversationMenuStore
}

export const ConversationHeader: FC<IConversationHeaderProps> = observer(
  ({
    pageLayoutStore,
    conversationSearchStore,
    callModalStore,
    contactCreateModalStore,
    conversationMenuStore,
  }) => {
    const { conversationStore, inboxesStore, smartViewsStore, usersStore, userPermissionsStore } =
      useEntitiesContext()
    const isShouldRequestAccess = !!usersStore.user?.isShouldRequestAccess
    const { selectInbox } = inboxesStore
    const { isMediumView, isLargeView, isMobileView } = layoutStore
    const { pathname } = useLocation()
    const isShowCalls = ['calls'].includes(pathname)
    const isHideHeader =
      layoutStore.isMobileView && conversationSearchStore.searchDropdownStore.hasFilters
    const loading = conversationMenuStore.loadingUser || !selectInbox
    const { onOpenSearch, onToggleSearch } = useConversationsPageLayoutContext()

    const title = useMemo(() => {
      if (selectInbox?.type === 'unified-inbox') {
        return inboxesStore.localInboxes.name
      }

      return selectInbox?.name
    }, [
      inboxesStore.inboxesList,
      smartViewsStore.itemsList,
      selectInbox,
      inboxesStore.localInboxes.currentInboxId,
    ])

    const subtitle = useMemo(() => {
      if (selectInbox instanceof Inbox && selectInbox.isRCS) {
        return ''
      }

      if (selectInbox?.type === 'unified-inbox') {
        if (inboxesStore.localInboxes.hasSmartViewFilter) {
          return 'Smart view'
        }

        return 'All inboxes'
      }

      let numbers: Phone[] = []
      if (selectInbox?.type === 'inbox_group') {
        numbers = inboxesStore.getNumbersByTeamIds(selectInbox.teamsIds)
      }

      if (selectInbox?.type === 'inbox') {
        numbers = selectInbox.numbers
      }

      if (numbers.length === 1) {
        return <PhoneRenderContent phone={numbers[0]} />
      }
      return `${numbers.length} numbers`
    }, [
      inboxesStore.inboxesList,
      smartViewsStore.itemsList,
      selectInbox,
      inboxesStore.localInboxes.currentInboxId,
    ])

    const customTitle = () => {
      if (
        conversationSearchStore.searchDropdownStore.hasFilters ||
        conversationSearchStore.opened
      ) {
        return (
          <Button
            typeBtn={'filter'}
            contained={'secondary'}
            text={'New filter'}
            badgeEnable
            badgeCount={conversationSearchStore.searchDropdownStore.filtersCount}
            icon={conversationSearchStore.opened ? 'chevronUp' : 'chevronDown'}
            size={'extraLarge'}
            badgeProps={{
              size: 'large',
            }}
            onClick={() => {
              onToggleSearch()
            }}
            isRight
          />
        )
      }

      if (isShouldRequestAccess) {
        return 'Request access'
      }

      const titleContent = (
        <div className={styles.titleContent}>
          <Typography
            variant={'body-lg-medium'}
            ellipsis
            ariaLabel={'title'}
            color={'var(--content-primary-primary)'}
          >
            {title}
          </Typography>
          <Icon
            className={styles.ellipse}
            icon={'ellipse_6'}
            fontSize={2}
            color={'var(--content-neutral-primary)'}
          />
          {subtitle && (
            <Typography
              variant={'body-md-regular'}
              ellipsis
              ariaLabel={'sub-title'}
              color={'tertiary'}
            >
              {subtitle}
            </Typography>
          )}
        </div>
      )

      if (IS_APP_LIGHT) {
        return (
          <Tooltip label={pageLayoutStore.isCollapse ? 'Show inboxes' : 'Hide inboxes'}>
            <button className={styles.titleBtn} onClick={pageLayoutStore.headerMenuIconAction}>
              <Icon {...pageLayoutStore.titleIconProps} count={inboxesStore.unreadMessagesCount} />
              {titleContent}
            </button>
          </Tooltip>
        )
      }

      return <div className={styles.titleContainer}>{titleContent}</div>
    }

    if (isHideHeader) return null

    return (
      <ContainerHeader
        customTitle={customTitle()}
        titleIconProps={IS_APP_LIGHT ? undefined : pageLayoutStore.titleIconProps}
        actionIconTooltipProps={
          IS_APP_LIGHT ? undefined : pageLayoutStore.headerMenuTooltipIconProps
        }
        titleIconAction={IS_APP_LIGHT ? undefined : pageLayoutStore.headerMenuIconAction}
        ellipsis
        isEmoji={!selectInbox?.isGroupInbox && !selectInbox?.isUnifiedInbox}
        isBorderBottom
        loading={loading}
        paddingLeft={16}
        ariaLabel='ActiveConversationName'
        actions={
          isLargeView || isMobileView
            ? undefined
            : [
                {
                  iconButtonComponent: (
                    <IconButton
                      onClick={() => {
                        if (isShowCalls) {
                          uiStore.changeRoute({
                            path: `/${ConversationRoutes.root}`,
                          })
                        }

                        isMediumView ? onOpenSearch() : onToggleSearch()
                      }}
                      icon={'search'}
                      disabled={isShouldRequestAccess}
                      transparentBackground={isShouldRequestAccess}
                      fontSize={20}
                      shape={EnumIconButtonShape.Rounded}
                      color={'secondary'}
                      variant={'icon'}
                      tooltipProps={
                        !isShouldRequestAccess
                          ? {
                              placement: 'bottom',
                              label: 'Search',
                              disableInteractive: true,
                            }
                          : undefined
                      }
                      ariaLabel='ConversationLayout_search'
                    />
                  ),
                },
                {
                  iconButtonComponent: (
                    <ConversationHeaderListDropdown
                      disabled={isShouldRequestAccess}
                      ariaLabel='AddNewMessageCall'
                      onChangeCall={() => {
                        callModalStore.openModal()
                      }}
                      onChangeNew={() => {
                        conversationStore.conversationOpenStore.handleOpenNewConversation()
                      }}
                      onChangeContactCreate={() => {
                        userPermissionsStore.getItem('can_create_contacts')
                          ? contactCreateModalStore.openModal()
                          : new FeatureRestrictedModalStore('Can create Contacts')
                      }}
                      onChangeSmartViewCreate={() => {
                        conversationMenuStore.smartViewsMenuStore.onOpenModalCreate()
                      }}
                    />
                  ),
                },
              ]
        }
        actionsGap={8}
      />
    )
  }
)
